import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import dayjs from 'dayjs'
import { Typography } from '@mui/material'
import { FormikDatePicker } from 'src/components/shared/form/formik/FormikDatePicker'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTimePicker } from 'src/components/shared/form/formik/FormikTimePicker'
import { customerProductLabels } from 'src/constants/customer-product/constants'
import type { CustomerProductName } from 'src/constants/customer/enums'
import type { CustomerProduct } from 'src/interfaces/customerProduct'
import { formatCustomerProductTimestamp } from 'src/utils/customerProductList'
import type {
  CustomerProductFormMode,
  CustomerProductFormValues,
} from './CustomerProductForm.interface'

interface Props {
  mode: CustomerProductFormMode
  product: CustomerProduct | null
  availableProducts: CustomerProductName[]
}

const CustomerProductFormContentComponent = ({ mode, product, availableProducts }: Props) => {
  const productOptions = useMemo(() => {
    return availableProducts.map((name) => {
      return { label: customerProductLabels[name], value: name }
    })
  }, [availableProducts])

  /**
   * Entitlements are not backdated, so the calendars start at today (§8.6). Built from the
   * UTC date rather than the host's, because these fields are labelled UTC — an admin west of
   * UTC late in the day would otherwise be offered a "today" that is already yesterday in the
   * clock the value is stored against. It is a local-mode `Dayjs` at midnight of that UTC
   * date, which is the frame MUI compares a freshly picked (and therefore local-mode) value in.
   *
   * The picker only greys the calendar out; typed input bypasses it, so the validator carries
   * the same floor.
   */
  const earliestStartDate = useMemo(() => {
    return dayjs(dayjs.utc().format('YYYY-MM-DD'))
  }, [])

  // On edit both the product and the start time are immutable server-side — `update` reads
  // only `productId` and `endTime` — so they are shown as facts rather than as fields the
  // admin can change and watch be discarded.
  if (mode === 'edit') {
    return (
      <div css={tw`space-y-4`}>
        <div>
          <Typography variant="h6" css={tw`text-shades-400`}>
            Product
          </Typography>
          <Typography variant="body2">
            {product ? customerProductLabels[product.product] : '—'}
          </Typography>
        </div>
        <div>
          <Typography variant="h6" css={tw`text-shades-400`}>
            Start time (UTC)
          </Typography>
          <Typography variant="body2">
            {(product && formatCustomerProductTimestamp(product.startTime)) || '—'}
          </Typography>
        </div>
        <div css={tw`flex flex-row gap-3`}>
          <FormikDatePicker<CustomerProductFormValues> name="endTime" label="End date (UTC)" />
          <FormikTimePicker<CustomerProductFormValues> name="endTime" label="End time (UTC)" />
        </div>
      </div>
    )
  }

  return (
    <div css={tw`space-y-4`}>
      <FormikSelect<CustomerProductFormValues>
        name="product"
        label="Product"
        placeholder="Select product"
        options={productOptions}
        data-testid="customer-product-form-product-select"
      />
      {/* Both pickers target one field: MUI X pickers each preserve the parts of the value
          they do not edit, so the date picker sets the day and the time picker the clock. */}
      <div css={tw`flex flex-row gap-3`}>
        <FormikDatePicker<CustomerProductFormValues>
          name="startTime"
          label="Start date (UTC)"
          minDate={earliestStartDate}
        />
        <FormikTimePicker<CustomerProductFormValues> name="startTime" label="Start time (UTC)" />
      </div>
      <div css={tw`flex flex-row gap-3`}>
        <FormikDatePicker<CustomerProductFormValues>
          name="endTime"
          label="End date (UTC)"
          minDate={earliestStartDate}
        />
        <FormikTimePicker<CustomerProductFormValues> name="endTime" label="End time (UTC)" />
      </div>
      <Typography variant="body2" css={tw`text-shades-500`}>
        Leave the end date empty for an ongoing product.
      </Typography>
    </div>
  )
}

export const CustomerProductFormContent = memo(CustomerProductFormContentComponent)
